import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * Player Schema
 * Represents a player within a team
 */

// Waiver details sub-schema
export const WaiverDetailSchema = z.object({
  sentDate: z.coerce.date().default(() => new Date()),
  signDate: z.coerce.date().optional(),
  accepted: z.boolean().optional(),
  waiver: objectId,
  signedName: z.string().optional(),
  // Relationship of the signer to the player (e.g. parent/guardian signing for a minor)
  signedByRelationship: z.string().optional(),
});

export type WaiverDetail = z.infer<typeof WaiverDetailSchema>;

export const PlayerSchema = z.object({
  _id: objectId,
  name: z.string().default(''),
  email: z.string().default(''),
  number: z.string().default(''),
  info: z.any().optional(), // Mixed type for flexible data
  waiverDetails: z.array(WaiverDetailSchema).default([]),
  // Refs
  team: objectId, // DivisionTeam
  organization: objectId,
  tournament: objectId,
  league: objectId,
  division: objectId,
  registrationTeam: objectId,
  registration: objectId,
  user: objectId,
  ...timestampFields,
});

export type Player = z.infer<typeof PlayerSchema>;

// Metadata for Player fields (for Mongoose refs and UI)
export const playerMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  name: { label: 'Player Name', editable: true },
  email: { label: 'Email', editable: true },
  number: { label: 'Number', editable: true },
  info: { label: 'Info', hidden: true },
  waiverDetails: { label: 'Waiver Details' },
  team: { ref: 'DivisionTeam', label: 'Team' },
  organization: { ref: 'Organization', hidden: true },
  tournament: { ref: 'Tournament', label: 'Tournament' },
  league: { ref: 'League', label: 'League' },
  division: { ref: 'Division', label: 'Division' },
  registrationTeam: { ref: 'RegistrationTeam', label: 'Registration Team' },
  registration: { ref: 'Registration', label: 'Registration' },
  user: { ref: 'User', label: 'User' },
  created: { hidden: true },
  updated: { hidden: true },
};

// Metadata for waiver sub-document
export const waiverDetailMetadata: Record<string, FieldMetadata> = {
  waiver: { ref: 'Waiver' },
};
